import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { ArrowLeftIcon } from '@heroicons/react/24/outline';
import { Link, useNavigate } from 'react-router-dom';
import { useLocation } from '../../../contexts/LocationContext';
import { useBusinessTypeContext } from '../../../contexts/BusinessTypeContext';
import { useMutation } from '../../../hooks/useApi';
import LoadingSpinner from '../../../components/UI/LoadingSpinner';
import apiClient, { getErrorMessage } from '../../../utils/api';
import { GoatMaster, GoatBreedingLog } from '../../../types/api';

interface KidDetail {
  gender: 'doe' | 'buck';
  birth_weight_kg?: number;
}

interface GoatKiddingFormData {
  doe_tag: string;
  kidding_date: string;
  kids_born: number;
  kid_tags?: string[];
  breeding_log_id?: string;
  kid_details?: KidDetail[];
}

// API payload type (matches backend KidDetail with category instead of gender)
interface KidDetailApi {
  category: 'doe' | 'buck';
  birth_weight_kg?: number;
}

interface GoatKiddingLogApiPayload {
  location_id?: string;
  doe_tag: string;
  kidding_date: string;
  kids_born: number;
  kid_tags?: string[];
  breeding_log_id?: string;
  kid_details?: KidDetailApi[];
}

const kiddingSchema = yup.object({
  doe_tag: yup.string().required('Doe tag is required'),
  kidding_date: yup.string().required('Kidding date is required'),
  kids_born: yup.number().integer().min(0).required('Number of kids born is required'),
  kid_tags: yup.array().of(yup.string()).optional(),
  kid_details: yup.array().of(
    yup.object({
      gender: yup.string().oneOf(['doe', 'buck']).required('Gender is required'),
      birth_weight_kg: yup.number().positive('Birth weight must be positive').optional(),
    })
  ).optional().test('length-match', 'Kid details must match number of kids born', function(value) {
    const kidsBorn = this.parent.kids_born;
    if (!value || kidsBorn === 0) return true;
    return value.length === kidsBorn;
  }),
});

const GoatKiddingForm: React.FC = () => {
  const navigate = useNavigate();
  const { selectedLocation } = useLocation();
  const { businessType } = useBusinessTypeContext();
  const [error, setError] = useState('');
  const [does, setDoes] = useState<GoatMaster[]>([]);
  const [loadingGoats, setLoadingGoats] = useState(true);
  const [breedingLogs, setBreedingLogs] = useState<GoatBreedingLog[]>([]);
  const [loadingBreedingLogs, setLoadingBreedingLogs] = useState(false);
  const [selectedBreedingLogId, setSelectedBreedingLogId] = useState<string>('');

  const { mutate: createKiddingLog, loading: creating } = useMutation<any, GoatKiddingLogApiPayload>();

  const form = useForm<GoatKiddingFormData>({
    resolver: yupResolver(kiddingSchema) as any,
    defaultValues: {
      doe_tag: '',
      kidding_date: new Date().toISOString().split('T')[0],
      kids_born: 1,
      kid_tags: [],
      breeding_log_id: '',
      kid_details: [],
    },
  });

  const watchedDoeTag = form.watch('doe_tag');
  const watchedKiddingDate = form.watch('kidding_date');
  const watchedKidsBorn = form.watch('kids_born');
  const watchedKidDetails = form.watch('kid_details');

  useEffect(() => {
    if (businessType === 'goat' && selectedLocation?.id) {
      loadGoats();
    }
  }, [selectedLocation?.id, businessType]);

  // Load breeding logs when doe is selected
  useEffect(() => {
    if (watchedDoeTag && selectedLocation?.id) {
      setSelectedBreedingLogId(''); // Reset selection when doe changes
      form.setValue('breeding_log_id', '');
      loadBreedingLogs(watchedDoeTag);
    } else {
      setBreedingLogs([]);
      setSelectedBreedingLogId('');
      form.setValue('breeding_log_id', '');
    }
  }, [watchedDoeTag, selectedLocation?.id]);

  // Auto-select breeding log based on expected_kidding date
  useEffect(() => {
    if (breedingLogs.length > 0 && watchedKiddingDate && watchedDoeTag && !selectedBreedingLogId) {
      const kiddingDate = new Date(watchedKiddingDate);
      // Find breeding log with expected_kidding date closest to kidding_date (within ±30 days)
      let bestMatch: GoatBreedingLog | null = null;
      let minDiff = Infinity;

      for (const log of breedingLogs) {
        if (log.expected_kidding) {
          const expectedDate = new Date(log.expected_kidding);
          const diff = Math.abs(kiddingDate.getTime() - expectedDate.getTime());
          const daysDiff = diff / (1000 * 60 * 60 * 24);
          
          if (daysDiff <= 30 && daysDiff < minDiff) {
            minDiff = daysDiff;
            bestMatch = log;
          }
        }
      }

      if (bestMatch !== null) {
        const breedingLogId = bestMatch.id;
        setSelectedBreedingLogId(breedingLogId);
        form.setValue('breeding_log_id', breedingLogId);
      }
    }
  }, [breedingLogs, watchedKiddingDate, watchedDoeTag, selectedBreedingLogId, form]);

  // Initialize/update kid_details array when kids_born changes
  useEffect(() => {
    const kidsBorn = watchedKidsBorn || 0;
    const currentDetails = watchedKidDetails || [];
    
    if (kidsBorn > 0) {
      // Create or update kid_details array to match kids_born count
      const newDetails: KidDetail[] = [];
      for (let i = 0; i < kidsBorn; i++) {
        if (currentDetails[i]) {
          // Keep existing data
          newDetails.push(currentDetails[i]);
        } else {
          // Initialize new entry
          newDetails.push({ gender: 'doe' }); // Default to 'doe'
        }
      }
      form.setValue('kid_details', newDetails);
    } else {
      form.setValue('kid_details', []);
    }
  }, [watchedKidsBorn]); // Only depend on watchedKidsBorn to avoid infinite loops

  const loadGoats = async () => {
    try {
      setLoadingGoats(true);
      const response = await apiClient.get(`/goat/master?location_id=${selectedLocation?.id}`);
      const allGoats = response.data || [];
      setDoes(allGoats.filter((g: GoatMaster) => g.category === 'doe' && g.status === 'active'));
    } catch (err) {
      console.error('Failed to load goats:', err);
    } finally {
      setLoadingGoats(false);
    }
  };

  const loadBreedingLogs = async (doeTag: string) => {
    try {
      setLoadingBreedingLogs(true);
      const response = await apiClient.get(
        `/goat/breeding-logs/for-doe/${doeTag}?location_id=${selectedLocation?.id}&pregnancy_check=true`
      );
      setBreedingLogs(response.data || []);
    } catch (err) {
      console.error('Failed to load breeding logs:', err);
      setBreedingLogs([]);
    } finally {
      setLoadingBreedingLogs(false);
    }
  };


  const handleSubmit = async (data: GoatKiddingFormData) => {
    try {
      setError('');
      // Transform kid_details from {gender, birth_weight_kg} to {category, birth_weight_kg} for backend
      const kid_details: KidDetailApi[] | undefined = data.kid_details && data.kid_details.length > 0
        ? data.kid_details.map(kd => ({
            category: kd.gender as 'doe' | 'buck', // gender is 'doe' or 'buck', which matches category
            birth_weight_kg: kd.birth_weight_kg
          }))
        : undefined;
      
      const kiddingData: GoatKiddingLogApiPayload = {
        doe_tag: data.doe_tag,
        kidding_date: data.kidding_date,
        kids_born: data.kids_born,
        location_id: selectedLocation?.id,
        kid_tags: [], // Tags will be auto-generated by backend
        breeding_log_id: selectedBreedingLogId || undefined,
        kid_details: kid_details,
      };
      await createKiddingLog('/goat/kidding-logs', kiddingData);
      navigate('/goat/kidding-logs');
    } catch (err: any) {
      setError(getErrorMessage(err, 'Failed to create kidding record'));
    }
  };

  const selectedBreedingLog = breedingLogs.find(log => log.id === selectedBreedingLogId);

  if (businessType !== 'goat') {
    return (
      <div className="container-mobile py-8 text-center">
        <p className="text-gray-600">This form is only available for goat farming locations.</p>
        <Link to="/dashboard" className="text-blue-600 hover:text-blue-800">Go to Dashboard</Link>
      </div>
    );
  }

  if (!selectedLocation) {
    return (
      <div className="container-mobile py-8 text-center">
        <p className="text-gray-600">Please select a location first</p>
        <Link to="/locations" className="text-blue-600 hover:text-blue-800">Go to Location Selection</Link>
      </div>
    );
  }

  return (
    <div className="container-mobile py-6 space-y-6">
      {/* Header */}
      <div className="flex items-center space-x-4">
        <Link to="/goat/kidding-logs" className="btn-touch p-2 text-gray-400 hover:text-gray-600">
          <ArrowLeftIcon className="h-5 w-5" />
        </Link>
        <div>
          <h1 className="text-2xl font-bold text-gray-900">🐐 Record Kidding</h1>
          <p className="text-gray-600">{selectedLocation.name}</p>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-800">{error}</p>
        </div>
      )}

      {/* Form */}
      <div className="card p-6">
        <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="form-label">Doe Tag *</label>
              {loadingGoats ? (
                <div className="form-input bg-gray-50 flex items-center">
                  <LoadingSpinner size="sm" />
                  <span className="ml-2 text-sm text-gray-500">Loading does...</span>
                </div>
              ) : (
                <select {...form.register('doe_tag')} className="form-input">
                  <option value="">Select Doe</option>
                  {does.map((doe) => (
                    <option key={doe.id} value={doe.tag_no}>
                      {doe.tag_no} {doe.breed ? `(${doe.breed})` : ''}
                    </option>
                  ))}
                </select>
              )}
              {form.formState.errors.doe_tag && (
                <p className="mt-1 text-sm text-red-600">{form.formState.errors.doe_tag.message}</p>
              )}
            </div>

            <div>
              <label className="form-label">Kidding Date *</label>
              <input
                type="date"
                {...form.register('kidding_date')}
                className="form-input"
              />
              {form.formState.errors.kidding_date && (
                <p className="mt-1 text-sm text-red-600">{form.formState.errors.kidding_date.message}</p>
              )}
            </div>

            <div>
              <label className="form-label">Number of Kids Born *</label>
              <input
                type="number"
                min="0"
                {...form.register('kids_born')}
                className="form-input"
                placeholder="0"
              />
              {form.formState.errors.kids_born && (
                <p className="mt-1 text-sm text-red-600">{form.formState.errors.kids_born.message}</p>
              )}
            </div>

            {/* Kid Details Table */}
            {watchedKidsBorn > 0 && (
              <div className="sm:col-span-2">
                <label className="form-label">Kid Details *</label>
                <div className="overflow-x-auto -mx-2 sm:mx-0">
                  <div className="inline-block min-w-full align-middle">
                    <table className="min-w-full divide-y divide-gray-200 border border-gray-300 rounded-lg">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-3 sm:px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider min-w-[80px]">
                            Kid #
                          </th>
                          <th className="px-3 sm:px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider min-w-[120px]">
                            Gender *
                          </th>
                          <th className="px-3 sm:px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider min-w-[140px]">
                            Birth Weight (kg)
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {Array.from({ length: watchedKidsBorn }).map((_, index) => (
                          <tr key={index}>
                            <td className="px-3 sm:px-4 py-3 text-sm font-medium text-gray-900">
                              Kid {index + 1}
                            </td>
                            <td className="px-3 sm:px-4 py-3">
                              <select
                                className="form-input text-sm w-full min-w-[100px]"
                                value={watchedKidDetails?.[index]?.gender || 'doe'}
                                onChange={(e) => {
                                  const newDetails = [...(watchedKidDetails || [])];
                                  if (!newDetails[index]) {
                                    newDetails[index] = { gender: 'doe' };
                                  }
                                  newDetails[index].gender = e.target.value as 'doe' | 'buck';
                                  form.setValue('kid_details', newDetails, { shouldValidate: true });
                                }}
                              >
                                <option value="doe">Doe</option>
                                <option value="buck">Buck</option>
                              </select>
                              {form.formState.errors.kid_details?.[index]?.gender && (
                                <p className="mt-1 text-xs text-red-600">
                                  {form.formState.errors.kid_details[index]?.gender?.message}
                                </p>
                              )}
                            </td>
                            <td className="px-3 sm:px-4 py-3">
                              <input
                                type="number"
                                step="0.1"
                                min="0"
                                className="form-input text-sm w-full min-w-[120px]"
                                placeholder="Optional"
                                value={watchedKidDetails?.[index]?.birth_weight_kg || ''}
                                onChange={(e) => {
                                  const newDetails = [...(watchedKidDetails || [])];
                                  if (!newDetails[index]) {
                                    newDetails[index] = { gender: watchedKidDetails?.[index]?.gender || 'doe' };
                                  }
                                  const weightValue = e.target.value === '' ? undefined : parseFloat(e.target.value);
                                  if (isNaN(weightValue || 0)) {
                                    newDetails[index].birth_weight_kg = undefined;
                                  } else {
                                    newDetails[index].birth_weight_kg = weightValue;
                                  }
                                  form.setValue('kid_details', newDetails, { shouldValidate: true });
                                }}
                              />
                              {form.formState.errors.kid_details?.[index]?.birth_weight_kg && (
                                <p className="mt-1 text-xs text-red-600">
                                  {form.formState.errors.kid_details[index]?.birth_weight_kg?.message}
                                </p>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
                {form.formState.errors.kid_details && typeof form.formState.errors.kid_details.message === 'string' && (
                  <p className="mt-1 text-sm text-red-600">{form.formState.errors.kid_details.message}</p>
                )}
                <p className="mt-1 text-xs text-gray-500">
                  Select gender (Buck/Doe) for each kid. Birth weight is optional and can be added later.
                </p>
              </div>
            )}

            {/* Breeding Log Selection */}
            {watchedDoeTag && (
              <div className="sm:col-span-2">
                <label className="form-label">Breeding Record (Optional)</label>
                {loadingBreedingLogs ? (
                  <div className="form-input bg-gray-50 flex items-center">
                    <LoadingSpinner size="sm" />
                    <span className="ml-2 text-sm text-gray-500">Loading breeding records...</span>
                  </div>
                ) : breedingLogs.length > 0 ? (
                  <>
                    <select
                      value={selectedBreedingLogId}
                      onChange={(e) => {
                        setSelectedBreedingLogId(e.target.value);
                        form.setValue('breeding_log_id', e.target.value || undefined);
                      }}
                      className="form-input"
                    >
                      <option value="">Select Breeding Record (Optional)</option>
                      {breedingLogs.map((log) => (
                        <option key={log.id} value={log.id}>
                          Buck: {log.buck_tag} | Breeding: {new Date(log.breeding_date).toLocaleDateString()}
                          {log.expected_kidding ? ` | Expected: ${new Date(log.expected_kidding).toLocaleDateString()}` : ''}
                        </option>
                      ))}
                    </select>
                    {selectedBreedingLog && (
                      <p className="mt-1 text-xs text-gray-600">
                        Selected: Buck {selectedBreedingLog.buck_tag} | Breeding Date: {new Date(selectedBreedingLog.breeding_date).toLocaleDateString()}
                        {selectedBreedingLog.expected_kidding && ` | Expected Kidding: ${new Date(selectedBreedingLog.expected_kidding).toLocaleDateString()}`}
                      </p>
                    )}
                    <p className="mt-1 text-xs text-gray-500">
                      Kid tags will be auto-generated (K01, K02, K03...) and shown in the listing
                    </p>
                  </>
                ) : (
                  <div className="form-input bg-gray-50 text-sm text-gray-500">
                    No positive breeding records found for this doe
                  </div>
                )}
              </div>
            )}
          </div>

          <div className="flex space-x-4 pt-4">
            <button
              type="submit"
              disabled={creating}
              className="flex-1 btn-touch bg-green-600 text-white py-3 rounded-lg hover:bg-green-700 disabled:opacity-50 flex items-center justify-center"
            >
              {creating ? <LoadingSpinner size="sm" /> : 'Record Kidding'}
            </button>
            <Link
              to="/goat/kidding-logs"
              className="px-6 py-3 border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              Cancel
            </Link>
          </div>
        </form>
      </div>
    </div>
  );
};

export default GoatKiddingForm;
